import type { AlarmEntry } from '@/components/dashboard/alarmTypes';

/** A row of the video stream registry — what /api/video/streams returns. */
export interface CopVideoStream {
  /** Unique per CAMERA: `{droneId}:{cameraId}`, or a bare droneId for an
   *  aircraft with one unnamed camera. */
  id: string;
  drone_id: string;
  /** Which camera on the airframe, e.g. "nose" | "down". Null when the
   *  aircraft registered a single unnamed feed. */
  camera_id?: string | null;
  /** Operator-facing name for the camera. */
  label?: string | null;
  stream_url: string;
  type: 'rtmp' | 'webrtc' | 'hls';
  status: string;
  resolution?: string | null;
  latency_ms?: number | null;
  updated_at?: string;
}

export interface ActiveMission {
  id: string;
  name: string;
  status: string;
  assigned_drone_id: string | null;
  drone_callsign: string | null;
  /**
   * 022/#700. What the COP needs to draw an ORBIT's ring on the live picture.
   *
   * Optional because every other producer of this shape (draft previews) predates them, and a
   * missing radius simply draws no ring - the same answer as a non-orbit.
   */
  type?: string | null;
  orbit_radius_m?: number | null;
  orbit_direction?: 'cw' | 'ccw' | null;
  waypoints: {
    latitude: number;
    longitude: number;
    altitude: number;
    action: string;
    sequence_order: number;
  }[];
}

export interface AlertApiRow {
  id: string;
  org_id: string;
  /** Nullable in the schema (`ON DELETE SET NULL`), so consumers must fall back. */
  rule_id?: string | null;
  drone_id: string | null;
  severity: string;
  title: string;
  message: string;
  status: string;
  drone_callsign?: string;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

export function toAlarmEntry(row: AlertApiRow): AlarmEntry {
  return {
    id: row.id,
    rule_id: row.rule_id ?? null,
    severity: (row.severity === 'critical' || row.severity === 'warning' || row.severity === 'info')
      ? row.severity
      : 'info',
    title: row.title,
    message: row.message,
    drone_id: row.drone_id,
    drone_callsign: row.drone_callsign ?? null,
    status: row.status === 'acknowledged' ? 'acknowledged' : row.status === 'resolved' ? 'resolved' : 'active',
    created_at: row.created_at,
    acknowledged_at: row.acknowledged_at,
    resolved_at: row.resolved_at,
  };
}
